import { useState } from 'react';
import { useNavigate, Navigate, Link, useSearch } from '@tanstack/react-router'
import { isSignedIn, handleGoogleSignIn, signup } from '../auth';
import { GoogleLogin } from '@react-oauth/google';
import { useTheme } from '../hooks/useTheme';
import { Button } from '../components/Button';
import { TextInput } from '../components/TextInput';
import { useSEO } from '../hooks/useSEO';

export default function SignUpForm() {
  useSEO({ title: 'Sign up – Compress GLB', robots: 'noindex, follow' });

  const navigate = useNavigate({ from: '/sign-up' });
  const search = useSearch({ from: '/sign-up' });
  const [apiError, setApiError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  useTheme(); // Initialize theme

  const handleGoogleSuccess = (credentialResponse) => {
    handleGoogleSignIn(credentialResponse, navigate);
  };

  const handleGoogleError = () => {
    console.error("Google Sign Up Failed");
    setApiError('Google Sign-Up failed. Please try again.');
  };

  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    setApiError('');
    setSuccessMessage('');
    setIsLoading(true);

    try {
      const data = await signup(email, password);
      setSuccessMessage(data.message || 'Account created! Please check your email to verify.');
      setEmail('');
      setPassword('');

      // If there's a redirect param (e.g. from generator page), we can't auto-redirect 
      // until they verify email, but we can show a message
      if (search.redirect) {
        // Store redirect for later use after verification if needed, 
        // or just inform them they can continue after verification.
        localStorage.setItem('post_login_redirect', search.redirect);
      }

    } catch (err) {
      setApiError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSignedIn()) {
    // If already signed in, respect the redirect param if present
    if (search.redirect) {
      return <Navigate to={search.redirect} />;
    }
    return <Navigate to="/app/dashboard" />;
  }

  return (
    <main className="bg-background w-screen min-h-screen flex justify-center items-center px-4 py-10 text-foreground">
      <div className="w-full max-w-5xl grid gap-8 lg:grid-cols-2 lg:gap-12">
        {/* Signup Card */}
        <section className="order-2 lg:order-1 bg-background-elevated p-8 md:p-10 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 space-y-6 border border-border/60">
          <header className="space-y-3 text-center lg:text-left">
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              Optimize & deliver 3D up to <span className="text-accent">90% smaller</span>
            </h1>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Compression for GLB / glTF that preserves visual fidelity while reducing load, bandwidth & storage.
            </p>
          </header>

          {apiError && (
            <div
              role="alert"
              aria-label="sign-up error"
              className="bg-danger/10 border border-danger/20 text-danger px-4 py-3 rounded-lg text-sm"
            >
              {apiError}
            </div>
          )}

          {successMessage && (
            <div
              role="alert"
              aria-label="sign-up success"
              className="bg-success/10 border border-success/20 text-success px-4 py-3 rounded-lg text-sm"
            >
              {successMessage}
            </div>
          )}

          <div className="space-y-6">
            {/* Google Sign In - Top Priority */}
            <div className="flex justify-center lg:justify-start w-full">
              <div className="w-full flex justify-center">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  width="100%"
                />
              </div>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border/60" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background-elevated px-2 text-muted-foreground">
                  Or sign up with email
                </span>
              </div>
            </div>

            {/* Email Form */}
            <form onSubmit={handleEmailSignUp} className="space-y-4">
              <TextInput
                id="email"
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
              />
              <TextInput
                id="password"
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                minLength={8}
                showPasswordToggle
              />
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? 'Creating account...' : 'Sign up'}
              </Button>

              <p className="text-xs leading-relaxed text-muted-foreground text-center">
                By signing up you agree to our{' '}
                <a href="/terms" className="text-accent hover:text-accent/90 underline decoration-accent/30 underline-offset-2 transition-colors">
                  Terms
                </a>
                {' '}& {' '}
                <a href="/privacy" className="text-accent hover:text-accent/90 underline decoration-accent/30 underline-offset-2 transition-colors">
                  Privacy Policy
                </a>
                .
              </p>
            </form>
          </div>

          <div className="pt-2 border-t border-border/60">
            <p className="text-sm text-center text-muted-foreground">
              Already have an account?{' '}
              <Link to="/sign-in" className="text-accent hover:text-accent/90 font-medium transition-colors">
                Sign in
              </Link>
            </p>
          </div>
        </section>

        {/* Social Proof / Credibility */}
        <aside className="order-1 lg:order-2 space-y-6">
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Users', value: '6,000+' },
                { label: 'Models Compressed', value: '50,000+' },
                { label: 'Storage Saved', value: '1+ TB' },
                { label: 'Compression', value: 'Up to 90%' }
              ].map(m => (
                <div key={m.label} className="rounded-xl border border-border/60 bg-background-elevated p-5 shadow-sm hover:shadow-md hover:border-border transition-all duration-200">
                  <p className="text-2xl font-bold text-accent tabular-nums tracking-tight">{m.value}</p>
                  <p className="mt-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{m.label}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 text-[10px] font-semibold text-muted-foreground">
              {['Privacy First', 'No Spam', 'Instant Access', 'API Ready'].map(b => (
                <span key={b} className="px-3 py-1.5 rounded-full border border-border/60 bg-background-soft tracking-wide uppercase hover:border-border hover:bg-background-elevated transition-all duration-200">
                  {b}
                </span>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}
